import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable"
import { ChatArea, TextSize } from '@/components/chat/ChatArea';
import { useState } from "react";
import { PanelLeft, PanelLeftClose, PictureInPicture, Volume2, Settings, Mic } from 'lucide-react';
import { cn } from "@/lib/utils";
import { useTranslation } from "@/lib/translate";
import { useAppStore } from "@/hooks/useAppStore";
import { useEffect, memo } from "react";
import ExitPresentationIcon from "../icons/ExitPresentation";
import { Button } from "../ui/button";
import { useMicButtonState } from "../layout/MicButton";
import { useIsMobile } from "@/hooks/useIsMobile";
import { DisplayMessage } from "@/types";
import { useTheme } from "../themes/ThemeProvider";

export const FONT_SIZES: TextSize[] = ["xl", "4xl", "6xl"];

export const SIZE_CLASSES: Record<TextSize, string> = {
  base: "text-xs",
  "2xl": "text-lg",
  "4xl": "text-lg",
  "6xl": "text-2xl",
  lg: "text-sm",
  xl: "text-sm"
};

const openAdmin = () => {
  const roomId: string | null = (new URLSearchParams(window.location.search)).get("r");
  window.open(`/admin?r=${roomId || ""}`);
}

export interface LayoutToolbarProps {
  exitPresentationMode: () => void;
  showSidebar: boolean;
  toggleSidebar: () => void;
  isAutoTTS: boolean;
  toggleAutoTTS: () => void;
  pipWindow: Window | null;
  togglePIP: () => void;
  fontSize: TextSize;
  setFontSize: (size: TextSize) => void;
  clientConfig: any;
  t: (key: string, params?: Record<string, any>) => string;
  theme: string;
  onToggleRecording: () => void;
  isRecording: boolean;
  isVADEnabled: boolean;
}

export const LayoutToolbar = memo(function LayoutToolbar({
  exitPresentationMode,
  showSidebar,
  toggleSidebar,
  isAutoTTS,
  toggleAutoTTS,
  pipWindow,
  togglePIP,
  fontSize,
  setFontSize,
  clientConfig,
  t,
  theme,
  onToggleRecording,
  isRecording,
  isVADEnabled,
}: LayoutToolbarProps) {

  const isMobile = useIsMobile();
  const logoWidth = isMobile ? 100 : (clientConfig?.config?.logoWidth || 150);

  const logoSrc = theme === 'dark' && clientConfig?.config?.logoDark
    ? clientConfig.config.logoDark
    : clientConfig?.config?.logo;

  return (
    <div className="p-1 flex flex-row items-center justify-between border-b border-neutral-500/30 bg-gray-200 dark:bg-black px-4">
      <img
        src={logoSrc}
        width={logoWidth}
      />
      <div className="flex items-center gap-1">
        {/* Exit */}
        {/* <Button
          variant="ghost"
          size="icon"
          onClick={exitPresentationMode}
          className={cn("rounded hover:bg-muted transition-colors")}
          title={t("Exit Presentation Mode") + " [F5]"}
        >
          <ExitPresentationIcon />
        </Button> */}

        <div className="hidden md:inline-flex h-6 w-px bg-muted/50 mx-2" />

        {/* Manual Recording Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleRecording}
          disabled={isVADEnabled}
          className={cn(
            "rounded hover:bg-muted transition-colors",
            isRecording ? "text-red-500 hover:text-red-600 bg-red-100/10" : "",
            isVADEnabled ? "opacity-30 cursor-not-allowed" : ""
          )}
          title={isVADEnabled 
            ? t("Manual recording disabled when Auto Mode is active") 
            : isRecording 
              ? t("Stop Recording") 
              : t("Start Recording")
          }
        >
          <Mic className={cn("w-4 h-4", isRecording && "fill-current")} />
        </Button>

        {/* Sidebar toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSidebar}
          className={cn(
            "hidden md:inline-flex rounded hover:bg-muted transition-colors",
            showSidebar ? "bg-muted" : ""
          )}
          title={showSidebar ? t("Hide Sidebar") : t("Show Sidebar")}
        >
          {showSidebar ? <PanelLeftClose size={15} /> : <PanelLeft size={15} />}
        </Button>

        {/* Auto TTS */}
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "rounded transition-colors relative z-10 hover:bg-muted",
            isAutoTTS ? "text-green-400" : ""
          )}
          title={t(
            "If enabled, automatically plays text-to-speech audio for the latest translated message."
          )}
          onClick={toggleAutoTTS}
        >
          <Volume2 className="w-4 h-4" />
        </Button>

        {/* PiP */}
        <Button
          variant="ghost"
          size="icon"
          onClick={togglePIP}
          className={cn(
            "rounded transition-colors",
            pipWindow
              ? "bg-muted text-green-400"
              : "hover:bg-muted"
          )}
          title={
            pipWindow
              ? t("Close Picture-in-Picture")
              : t("Open Picture-in-Picture Subtitles (Always on Top)")
          }
        >
          <PictureInPicture
            size={15}
            className={pipWindow ? "text-green-400" : ""}
          />
        </Button>

        {/* Font size */}
        {FONT_SIZES.map((size) => (
          <button
            key={size}
            onClick={() => setFontSize(size)}
            className={cn(
              "rounded px-1 ml-1 transition-colors",
              fontSize === size ? "bg-muted text-primary" : "hover:bg-muted"
            )}
            title={t(`Font size: {{size}}`, { size })}
          >
            <span className={`${SIZE_CLASSES[size]} leading-none`}>T</span>
          </button>
        ))}
        <Button
          variant="ghost"
          size="icon"
          className="rounded transition-colors relative z-10 hover:bg-muted"
          title="Open admin interface"
          onClick={openAdmin}
        >
          <Settings className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
});

// Resizable Chat Area for Presentation mode
export function PresentationChatLayout() {
  const preferredLanguage = useAppStore((state) => state.preferredLanguage);
  const targetLanguages = useAppStore((state) => state.targetLanguages);
  const t = useTranslation(preferredLanguage);

  const [showSidebar, setShowSidebar] = useState(false);
  const [fontSize, setFontSize] = useState<TextSize>("4xl");
  const [pipWindow, setPipWindow] = useState<Window | null>(null);
  const setPresentationMode = useAppStore((state) => state.setPresentationMode);
  const messages = useAppStore((state) => state.messages);
  const setPIPActive = useAppStore((state) => state.setPIPActive);
  const setAutoTTS = useAppStore((state) => state.setAutoTTS);
  const isAutoTTS = useAppStore((state) => state.isAutoTTS);
  const clientConfig = useAppStore((state) => state.clientConfig);
  const setVADEnabled = useAppStore((state) => state.setVADEnabled);
  const presentationSettings = useAppStore((state) => state.presentationSettings);
  const setPresentationSettings = useAppStore((state) => state.setPresentationSettings);
  const { resolvedTheme } = useTheme();

  // Sync local state with global presentation settings
  useEffect(() => {
    if (presentationSettings.fontSize) {
      setFontSize(presentationSettings.fontSize as TextSize);
    }
    setVADEnabled(presentationSettings.vadEnabled);
    setShowSidebar(presentationSettings.sidePanelOpen);
  }, [presentationSettings, setVADEnabled]);

  // Hook for manual recording (shared logic)
  const { isRecording, handleToggleRecording } = useMicButtonState({ enableAnimation: false });
  // VAD state to disable manual record button
  const isVADEnabled = useAppStore((state) => state.isVADEnabled);

  // PIP Functions
  const openPIP = async () => {
    if (pipWindow) {
      pipWindow.focus();
      return;
    }

    // Check if Document Picture-in-Picture API is supported
    if (!('documentPictureInPicture' in window)) {
      // Fallback to regular window.open for unsupported browsers
      openFallbackPIP();
      return;
    }

    try {
      // Calculate window dimensions for subtitle-style display
      const screenWidth = window.screen.width;
      const pipWidth = Math.min(800, screenWidth * 0.6); // 60% of screen width, max 800px
      const pipHeight = 120; // Fixed height for subtitle display

      // @ts-ignore - Document PiP API is newer and might not have types
      const newWindow = await window.documentPictureInPicture.requestWindow({
        width: pipWidth,
        height: pipHeight,
      });

      // Setup PIP window styles - chromeless and minimal
      newWindow.document.title = 'Chotto Subtitles';

      // Create and inject styles
      const style = newWindow.document.createElement('style');
      style.textContent = `
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          background-color: #0B1215;
          color: #ffffff;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          font-size: 24px;
          line-height: 1.4;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: space-between;
          height: 100vh;
          padding: 2px;
          overflow: hidden;
        }
        #subtitles {
          width: 100%;
          text-shadow: 2px 2px 4px rgba(0,0,0,0.8);
          word-wrap: break-word;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          margin-top: auto;
          margin-bottom: auto;
        }
        #footer {
          padding: 4px;
          font-size: 12px;
          opacity: 0.7;
          color: #36acb1;
        }
        #footer span {
          color: #5adfe4;
        } 
      `;
      newWindow.document.head.appendChild(style);

      // Add subtitle container
      const subtitleDiv = newWindow.document.createElement('div');
      subtitleDiv.id = 'subtitles';
      newWindow.document.body.appendChild(subtitleDiv);

      const footerDiv = newWindow.document.createElement('div');
      footerDiv.id = "footer";
      newWindow.document.body.appendChild(footerDiv);
      footerDiv.textContent = "Powered by ";

      const logo = newWindow.document.createElement("span");
      logo.textContent = 'chotto.chat'
      footerDiv.appendChild(logo)

      // Handle window close
      newWindow.addEventListener('pagehide', () => {
        setPipWindow(null);
        setPIPActive(false);
      });

      setPipWindow(newWindow);
      setPIPActive(true);
    } catch (error) {
      console.error('Failed to open Document PiP window:', error);
      // Fallback to regular window
      openFallbackPIP();
    }
  };

  // Fallback for browsers that don't support Document Picture-in-Picture
  const openFallbackPIP = () => {
    // Calculate window dimensions for subtitle-style display
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const pipWidth = Math.min(800, screenWidth * 0.6);
    const pipHeight = 120;
    const pipLeft = (screenWidth - pipWidth) / 2;
    const pipTop = screenHeight - pipHeight - 100;

    const newWindow = window.open(
      '',
      'ChottoPIP',
      `width=${pipWidth},height=${pipHeight},left=${pipLeft},top=${pipTop},resizable=yes,scrollbars=no,status=no,menubar=no,toolbar=no,location=no`
    );

    if (!newWindow) {
      alert('Pop-up blocked! Please allow pop-ups for this site.');
      return;
    }

    // Setup fallback PIP window
    newWindow.document.title = 'Chotto Subtitles';
    newWindow.document.body.style.margin = '0';
    newWindow.document.body.style.padding = '12px';
    newWindow.document.body.style.backgroundColor = '#0B1215';
    newWindow.document.body.style.color = '#ffffff';
    newWindow.document.body.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif';
    newWindow.document.body.style.fontSize = '24px';
    newWindow.document.body.style.lineHeight = '1.4';
    newWindow.document.body.style.textAlign = 'center';
    newWindow.document.body.style.display = 'flex';
    newWindow.document.body.style.alignItems = 'center';
    newWindow.document.body.style.justifyContent = 'center';
    newWindow.document.body.style.height = '100vh';
    newWindow.document.body.style.boxSizing = 'border-box';

    // Add subtitle container
    const subtitleDiv = newWindow.document.createElement('div');
    subtitleDiv.id = 'subtitles';
    subtitleDiv.style.width = '100%';
    subtitleDiv.style.textShadow = '2px 2px 4px rgba(0,0,0,0.8)';
    newWindow.document.body.appendChild(subtitleDiv);

    // Handle window close
    newWindow.addEventListener('beforeunload', () => {
      setPipWindow(null);
      setPIPActive(false);
    });

    setPipWindow(newWindow);
    setPIPActive(true);
  };

  const closePIP = () => {
    if (pipWindow) {
      pipWindow.close();
      setPipWindow(null);
      setPIPActive(false);
    }
  };

  // Update PIP window with latest messages
  useEffect(() => {
    if (!pipWindow || !pipWindow.document) return;

    const subtitleDiv = pipWindow.document.getElementById('subtitles');
    if (!subtitleDiv) return;

    // Get the latest translated message
    const latestMessage: DisplayMessage = messages.find(msg =>
      msg.type === 'message' &&
      msg.translations &&
      Object.keys(msg.translations).length > 0
    ) as DisplayMessage;

    if (!latestMessage) {
      return;
    }

    const displayLanguage = latestMessage.language == targetLanguages[0] ? preferredLanguage : targetLanguages[0];
    if (latestMessage && latestMessage.type === 'message') {
      const translation = latestMessage.translations[displayLanguage] || latestMessage.message;
      subtitleDiv.textContent = translation;
    } else {
      subtitleDiv.textContent = t('Waiting for translation...');
    }
  }, [messages, pipWindow, preferredLanguage]);

  // Cleanup PIP window on component unmount
  useEffect(() => {
    return () => {
      if (pipWindow) {
        pipWindow.close();
        setPIPActive(false);
      }
    };
  }, [pipWindow, setPIPActive]);

  // enable VAD by default
  useEffect(() => {
    // setVADEnabled(true); // Controlled by presentationSettings now
    setPresentationMode(true);
    return () => {
      setPresentationMode(false);
    }
  }, []);

  function exitPresentationMode() {
    const yes = window.confirm("Are you sure you want to exit presentation mode");
    if (yes) {
      setPresentationMode(false);
      setAutoTTS(false);
      setVADEnabled(false);
    }
  }

  function handleToggleSidebar() {
    const newState = !showSidebar;
    setShowSidebar(newState);
    setPresentationSettings({ sidePanelOpen: newState });
    return newState;
  }

  function handleToggleAutoTTS() {
    return setAutoTTS(!isAutoTTS);
  }

  function handleTogglePIP() {
    return pipWindow ? closePIP() : openPIP()
  }

  return (<>
    <LayoutToolbar
      exitPresentationMode={exitPresentationMode}
      showSidebar={showSidebar}
      toggleSidebar={handleToggleSidebar}
      // VAD props removed
      isAutoTTS={isAutoTTS}
      toggleAutoTTS={handleToggleAutoTTS}
      pipWindow={pipWindow}
      togglePIP={handleTogglePIP}
      fontSize={fontSize}
      setFontSize={(size) => {
        setFontSize(size);
        setPresentationSettings({ fontSize: size });
      }}
      clientConfig={clientConfig}
      t={t}
      theme={resolvedTheme}
      onToggleRecording={handleToggleRecording}
      isRecording={isRecording}
      isVADEnabled={isVADEnabled}
    />
    <ResizablePanelGroup
      direction="horizontal"
      className="min-h-[200px] md:min-w-[450px]">
      {showSidebar && <ResizablePanel
        id="sidebar"
        order={1}
        collapsible={false}
        defaultSize={25}
        minSize={25}
      >
        <div className="flex h-full items-center justify-center bg-black">
          <div className="relative flex h-full overflow-hidden w-full">
            <ChatArea filter="showOnlyOthers" />
          </div>
        </div>
      </ResizablePanel>}
      {<ResizableHandle withHandle />}
      <ResizablePanel
        id="main"
        order={2}
        defaultSize={75}>
        <div className="relative flex h-full items-center justify-center bg-black">
          {/* Show Original Text Toggle */}
          <div className="relative flex h-full overflow-hidden w-full">
            <ChatArea filter="showOnlyMine" showOriginalText={true} textSize={fontSize} isPresentationMode={true} />
          </div>
        </div>
      </ResizablePanel>
    </ResizablePanelGroup>
  </>
  )
}
